import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import DotIcon from "../icons/DotIcon";
import { InfoIconButton } from "../molecules/InfoIconButton";
import Line, { type Position } from "../molecules/Line";
import { type DocumentVersionSummaryChart } from "../helpers/chart";
import { useTranslation } from "react-i18next";
import DocumentIcon from "../molecules/DocumentIcon";
import { formatDate, isPastDate } from "~/helpers/date";
import cn from "classnames";
import { GripVertical } from "lucide-react";

interface Props {
  documentVersion: DocumentVersionSummaryChart;
  documentVersionMap: Map<number, DocumentVersionSummaryChart>;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  isDragging?: boolean;
  renderLines?: boolean;
  handleRef?: (element: Element | null) => void;
  reorderKey?: number;
}

interface PositionWithId extends Position {
  toId: number;
}

interface DocumentLinesProps {
  documentVersion: DocumentVersionSummaryChart;
  documentVersionMap: Map<number, DocumentVersionSummaryChart>;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  reorderKey?: number;
}

export const DocumentLines = ({
  documentVersion,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  hoveredId,
  reorderKey,
}: DocumentLinesProps) => {
  const [positions, setPositions] = useState<PositionWithId[]>([]);
  const { parentDocumentIds } = documentVersion.document;

  useEffect(() => {
    const measurePositions = () => {
      const newPositions: PositionWithId[] = [];
      const refsMap = documentRefsMap.current;

      parentDocumentIds.forEach((parentId) => {
        const parentDoc = documentVersionMap.get(parentId);
        const childRef = refsMap?.get(documentVersion.document.id);
        const parentRef = refsMap?.get(parentId);

        if (childRef && parentRef && parentDoc && containerRef?.current) {
          const parent = parentRef.getBoundingClientRect();
          const child = childRef.getBoundingClientRect();
          const container = containerRef.current.getBoundingClientRect();
          newPositions.push({
            parentX: parent.left - container.left,
            parentY: parent.bottom - container.top,
            childX: child.left - container.left,
            childY: child.top - container.top,
            toId: parentId,
          });
        }
      });

      // This effect synchronizes React state with DOM layout measurements to draw connecting lines between document cards
      setPositions(newPositions);
    };

    // After a reorder, dnd-kit's drop animation takes ~250ms while the
    // dragged card remains in the browser's popover top layer. The
    // Feedback plugin cleanup (which restores the card to normal document
    // flow) only runs after the animation finishes. Delay measurement so
    // we read final flow positions instead of popover-layer positions.
    if (reorderKey) {
      const timer = setTimeout(measurePositions, 300);
      return () => clearTimeout(timer);
    }

    measurePositions();
  }, [
    containerRef,
    parentDocumentIds,
    documentVersion.document.id,
    documentVersionMap,
    documentRefsMap,
    reorderKey,
  ]);

  const isHovering = hoveredId === documentVersion.document.id;

  return (
    <>
      {positions.map(({ toId, ...position }) => (
        <Line
          key={toId}
          {...position}
          offsetX={75}
          active={isHovering || hoveredId === toId}
        />
      ))}
    </>
  );
};

export const DocumentCard = ({
  documentVersion,
  documentVersionMap,
  documentRefsMap,
  containerRef,
  hoveredId,
  setHoveredId,
  setSelectedId,
  isDragging,
  renderLines = true,
  handleRef,
  reorderKey,
}: Props) => {
  const { t } = useTranslation();

  const { parentDocumentIds, childDocumentIds } = documentVersion.document;

  const isHovering = hoveredId === documentVersion.document.id;
  const isHoveringParent = parentDocumentIds.some((id) => hoveredId === id);
  const isHoveringChild = childDocumentIds.some((id) => hoveredId === id);

  const dateAttribute = isPastDate(documentVersion.adoptedAt)
    ? "adoptedAt"
    : "publishedAt";

  const formattedDate = formatDate(documentVersion[dateAttribute]);

  const dateLabel =
    dateAttribute === "adoptedAt"
      ? t("commons.document.adopted")
      : t("commons.documentStatus.published");

  return (
    <>
      {renderLines && (
        <DocumentLines
          documentVersion={documentVersion}
          documentVersionMap={documentVersionMap}
          documentRefsMap={documentRefsMap}
          containerRef={containerRef}
          hoveredId={hoveredId}
          reorderKey={reorderKey}
        />
      )}
      <div
        ref={(node) => {
          if (node) {
            documentRefsMap.current?.set(documentVersion.document.id, node);
          } else {
            documentRefsMap.current?.delete(documentVersion.document.id);
          }
        }}
        onMouseEnter={() => setHoveredId(documentVersion.document.id)}
        onMouseLeave={() => setHoveredId(undefined)}
        className={cn(
          "relative h-[150px] w-[150px]",
          isDragging &&
            "scale-[0.97] rounded-sm border-2 border-dashed border-green-600/50 bg-green-50/30 opacity-50",
        )}
      >
        {parentDocumentIds.length > 0 && (
          <div className="absolute -top-2 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center">
            <DotIcon active={isHovering || isHoveringParent} />
          </div>
        )}

        {handleRef && (
          <span
            ref={handleRef}
            className="absolute top-2 left-2 z-10 cursor-grab text-gray-400 hover:text-gray-600"
            aria-hidden="true"
          >
            <GripVertical className="h-4 w-4" />
          </span>
        )}

        <div className="absolute top-2 right-2">
          <InfoIconButton
            onClick={() => setSelectedId(documentVersion.document.id)}
          />
        </div>
        <Link
          to={`/documents/${documentVersion.document.id}?versionId=${documentVersion.id}&tab=metadata`}
          className={cn(
            "flex h-full cursor-pointer flex-col rounded-sm border-2 border-transparent bg-white p-2 shadow-md hover:border-green-600/40",
            isDragging && "pointer-events-none",
          )}
        >
          <div className="h-12 w-12 p-2.5">
            <DocumentIcon type={documentVersion.fileType} />
          </div>
          <div className="wrap-break-words line-clamp-4 flex-1 overflow-hidden text-xs font-medium hyphens-auto text-gray-700">
            {documentVersion.title}
          </div>
          <div className="text-[11px] text-gray-500">
            {`${dateLabel} ${formattedDate}`}
          </div>
        </Link>
        {childDocumentIds.length > 0 && (
          <div className="absolute -bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center">
            <DotIcon active={isHovering || isHoveringChild} />
          </div>
        )}
      </div>
    </>
  );
};
